import {
  DocumentNode,
  LazyQueryHookExecOptions,
  LazyQueryHookOptions,
  LazyQueryResultTuple,
  MutationFunctionOptions,
  MutationHookOptions,
  MutationTuple,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { useDttStore } from '../store';

type TransformerOptions<TCurrentContract, TFutureContract, TOptions> = {
  options?: TOptions;
  onTransform: (data: TCurrentContract) => TFutureContract;
  selectDttFlag?: (s: ReturnType<typeof useDttStore.getState>) => boolean;
};

type QueryTransformerOptions<TCurrentContract, TFutureContract, TOptions> = TransformerOptions<
  TCurrentContract,
  TFutureContract,
  TOptions
> & {
  currentQuery: DocumentNode;
  dttQuery: DocumentNode;
};

type MutationTransformerOptions<TCurrentContract, TFutureContract, TOptions> = TransformerOptions<
  TCurrentContract,
  TFutureContract,
  TOptions
> & {
  currentMutation: DocumentNode;
  dttMutation: DocumentNode;
};

function useTransformer<TCurrentContract, TFutureContract>(
  selectDttFlag: (s: ReturnType<typeof useDttStore.getState>) => boolean = s => s.isDtt
): [
  boolean,
  TFutureContract | undefined,
  (response: TFutureContract, onTransform: (response: TCurrentContract) => TFutureContract) => TFutureContract,
  () => void
] {
  const isDttFlow = useDttStore(selectDttFlag);
  const [transformedData, setTransformedData] = useState<TFutureContract>();

  const transformer = (
    response: TCurrentContract | TFutureContract,
    onTransform: (response: TCurrentContract) => TFutureContract
  ): TFutureContract => {
    const isDttFlow = selectDttFlag(useDttStore.getState());
    const clonedResponse = JSON.parse(JSON.stringify(response));
    const transformedResponse = isDttFlow
      ? (response as TFutureContract)
      : onTransform(clonedResponse as TCurrentContract);
    setTransformedData(transformedResponse);
    return transformedResponse;
  };

  const reset = () => {
    setTransformedData(undefined);
  };

  return [isDttFlow, transformedData, transformer, reset];
}

function useQueryTransformer<TCurrentContract, TFutureContract = TCurrentContract>({
  currentQuery,
  dttQuery,
  options = {},
  onTransform,
  selectDttFlag = s => s.isDtt,
}: QueryTransformerOptions<TCurrentContract, TFutureContract, QueryHookOptions>): QueryResult<
  TFutureContract,
  OperationVariables
> {
  const [isDttFlow, transformedData, transformer, reset] = useTransformer<TCurrentContract, TFutureContract>(
    selectDttFlag
  );
  const { onCompleted: onQueryCompleted, ...queryOptions } = options;

  const { data: _data, ...rest } = useQuery<TFutureContract>(isDttFlow ? dttQuery : currentQuery, {
    ...queryOptions,
    onCompleted: response => {
      const data = transformer(response, onTransform);
      onQueryCompleted?.(data);
    },
  });

  useEffect(() => {
    if (rest.loading) {
      reset();
    }
  }, [rest.loading, reset]);

  return { data: transformedData, ...rest };
}

function useLazyQueryTransformer<TCurrentContract, TFutureContract = TCurrentContract>({
  currentQuery,
  dttQuery,
  options = {},
  onTransform,
  selectDttFlag = s => s.isDtt,
}: QueryTransformerOptions<TCurrentContract, TFutureContract, LazyQueryHookOptions>): LazyQueryResultTuple<
  TFutureContract,
  OperationVariables
> {
  const [isDttFlow, transformedData, transformer, reset] = useTransformer<TCurrentContract, TFutureContract>(
    selectDttFlag
  );
  const { onCompleted: onQueryCompleted, ...queryOptions } = options;

  const [fetchDataFromQuery, { data, ...rest }] = useLazyQuery<TFutureContract>(isDttFlow ? dttQuery : currentQuery, {
    ...queryOptions,
    onCompleted(response) {
      const data = transformer(response, onTransform);
      onQueryCompleted?.(data);
    },
  });

  const fetchData = useCallback(
    (lazyQueryOptions: Partial<LazyQueryHookExecOptions<TFutureContract, OperationVariables>> = {}) => {
      const { onCompleted: onQueryCompleted, ...restLazyQueryOptions } = lazyQueryOptions;
      const formattedOptions: LazyQueryHookOptions = { ...restLazyQueryOptions };
      if (onQueryCompleted) {
        formattedOptions.onCompleted = response => {
          const data = transformer(response, onTransform);
          onQueryCompleted(data);
        };
      }
      return fetchDataFromQuery(formattedOptions);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (rest.loading) {
      reset();
    }
  }, [rest.loading, reset]);

  // to handle partial data + error when onCompleted callback function is not passed explicitly
  useEffect(() => {
    if (data && rest.error && !transformedData) {
      transformer(data, onTransform);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, rest.error, transformedData]);

  return [fetchData, { data: transformedData, ...rest }];
}

function useMutationTransformer<TCurrentContract, TFutureContract = TCurrentContract>({
  currentMutation,
  dttMutation,
  options = {},
  onTransform,
  selectDttFlag = s => s.isDtt,
}: MutationTransformerOptions<TCurrentContract, TFutureContract, MutationHookOptions>): MutationTuple<
  TFutureContract,
  OperationVariables
> {
  const [isDttFlow, transformedData, transformer] = useTransformer<TCurrentContract, TFutureContract>(selectDttFlag);
  const { onCompleted: onMutationCompleted, ...initialMutationOptions } = options;

  const [sendDataToMutation, { data: _data, ...rest }] = useMutation<TFutureContract>(
    isDttFlow ? dttMutation : currentMutation,
    {
      ...initialMutationOptions,
      onCompleted(response) {
        const data = transformer(response, onTransform);
        onMutationCompleted?.(data);
      },
    }
  );

  const sendData = useCallback(
    (mutationOptions: Partial<MutationFunctionOptions<TFutureContract, OperationVariables>> = {}) => {
      const { onCompleted: onMutationCompleted, ...restMutationOptions } = mutationOptions;
      const formattedOptions: MutationHookOptions = { ...restMutationOptions };
      if (onMutationCompleted) {
        formattedOptions.onCompleted = response => {
          const data = transformer(response, onTransform);
          onMutationCompleted(data);
        };
      }
      return sendDataToMutation(formattedOptions);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return [sendData, { data: transformedData, ...rest }];
}

export { useQueryTransformer, useLazyQueryTransformer, useMutationTransformer };
